import { Loader2 } from 'lucide-react';
import type {
  ProposalAcceptanceAction,
  ProposalAcceptanceHistoryItem,
} from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useProposalAcceptanceHistory } from '@/hooks/commercial/use-proposal-acceptance-history';

interface ResumeAcceptanceHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
}

const ACTION_LABELS: Record<ProposalAcceptanceAction, string> = {
  LINK_SENT: 'Código de acesso enviado',
  LINK_VIEWED: 'Acessou o link',
  OTP_SENT: 'Código enviado',
  OTP_RESENT: 'Solicitou novo código',
  OTP_VERIFIED: 'Código validado',
  DETAILS_VIEWED: 'Acessou detalhes da proposta',
  REFUSED: 'Recusou a proposta',
  ACCEPTED: 'Aceitou a proposta',
  EXPIRED: 'Link expirado',
};

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

function buildActionText(item: ProposalAcceptanceHistoryItem): string {
  const base = ACTION_LABELS[item.action] ?? item.action;
  if (item.action === 'REFUSED' && item.detail) {
    return `${base} - ${item.detail}`;
  }
  return base;
}

export function ResumeAcceptanceHistoryDialog({
  open,
  onClose,
  proposalId,
}: ResumeAcceptanceHistoryDialogProps) {
  const { data, error, isLoading, isError, refetch, isFetching } = useProposalAcceptanceHistory(
    proposalId,
    open,
  );

  const items = data ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Histórico de aceite</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : isError ? (
          <div className="space-y-3 py-8 text-center text-sm text-red-600">
            <p>
              {error instanceof Error ? error.message : 'Não foi possível carregar o histórico.'}
            </p>
            <Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Tentando novamente...' : 'Tentar novamente'}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>DATA/HORA</TableHead>
                <TableHead>E-MAIL</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>AÇÃO</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-gray-500">
                    Nenhum histórico de aceite encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={`${item.occurredAt}-${index}`}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(item.occurredAt)}
                    </TableCell>
                    <TableCell>{item.actorEmail || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{item.actorIp || '—'}</TableCell>
                    <TableCell>{buildActionText(item)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
